import React, {useContext, useState} from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import {CircularProgress} from "@mui/material";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {useAuth} from "../../hooks/useAuth";
import {useMutationUpdatePharmacy} from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import {NotificationType, SERVER_URL} from "../../constants/global";
import Typography from "@mui/material/Typography";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Link from "@mui/material/Link";

interface PharmacyInfoProps {
  pharmacy: any;
  statuses: any;
}

const PharmacyFiles:React.FunctionComponent<PharmacyInfoProps> = ({
  pharmacy,
  statuses,
}) => {
  const deaLicenseDefaultFile = pharmacy.PharmacyFiles.find((file: any) => file.File.type === 'deaLicense');
  const ncpdpLicenseDefaultFile = pharmacy.PharmacyFiles.find((file: any) => file.File.type === 'ncpdpLicense');
  const {addToast} = useContext(ToasterContext);
  const { token } = useAuth();
  const [deaLicenseFile, setDeaLicenseFile] = useState<any>(
    new File(
      [deaLicenseDefaultFile.File],
      deaLicenseDefaultFile.File.name,
      { type: deaLicenseDefaultFile.File.type }
      )
  );
  const [ncpdpLicenseFile, setNcpdpLicenseFile] = useState<any>(
    new File(
      [ncpdpLicenseDefaultFile.File],
      ncpdpLicenseDefaultFile.File.name,
      { type: ncpdpLicenseDefaultFile.File.type }
    )
  );
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      addToast({ 
        type: NotificationType.Error, 
        title: `File "${file.name}" is too large. Maximum size is 15 MB.` 
      });
      return false;
    }
    return true;
  };

  const handleDeaLicenseFileChange = (event: any) => {
    const {files} = event.target;
    if (!files[0]) return;

    if (!validateFileSize(files[0])) {
      event.target.value = ''; // Clear the file input
      return;
    }

    const newFile = new File([files[0]], `${Date.now()}_${files[0].name}`, {
      type: files[0].type,
    });

    setDeaLicenseFile(newFile);
  }
  const handleNcpdpLicenseFileChange = (event: any) => {
    const {files} = event.target;
    if (!files[0]) return;

    if (!validateFileSize(files[0])) {
      event.target.value = ''; // Clear the file input
      return;
    }

    const newFile = new File([files[0]], `${Date.now()}_${files[0].name}`, {
      type: files[0].type,
    });

    setNcpdpLicenseFile(newFile);
  }

  const handleSave = async () => {
    let pharmacyDeaLicenseFileId: number = 0;
    let pharmacyNcpdpLicenseFileId : number = 0;

    pharmacy.PharmacyFiles.forEach((pharmacyFile: any) => {
      if (pharmacyFile.File.type === 'deaLicense') {
        pharmacyDeaLicenseFileId = pharmacyFile.id;
      }
      if (pharmacyFile.File.type === 'ncpdpLicense') {
        pharmacyNcpdpLicenseFileId = pharmacyFile.id;
      }
    });

    try {
      setUploadLoading(true);
      const deaLicenseFileData = new FormData();
      deaLicenseFileData.append('file', deaLicenseFile);
      deaLicenseFileData.append('fileType', 'deaLicense');

      const ncpdpLicenseFileData = new FormData();
      ncpdpLicenseFileData.append('file', ncpdpLicenseFile);
      ncpdpLicenseFileData.append('fileType', 'ncpdpLicense');

      const deaLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: deaLicenseFileData,
        headers: {'Authorization': `Bearer ${token}`}
      });

      const ncpdpLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: ncpdpLicenseFileData,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (deaLicensePromise.ok && ncpdpLicensePromise.ok) {
        setUploadLoading(true);
        const deaLicenseDataRes = await deaLicensePromise.json();
        const ncpdpLicenseDataRes = await ncpdpLicensePromise.json();

        const deaLicenseRequestData = {
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: deaLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyDeaLicenseFileId
              }
            }
          },
          PharmacyStatus: {
            update: {
              Status: {
                connect: {
                  id: statuses.find((status: any) => status.key === 'LICENSE_UPLOADED').id
                }
              },
            }
          },
        }

        const ncpdpLicenseRequestData = {
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: ncpdpLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyNcpdpLicenseFileId
              }
            }
          },
          PharmacyStatus: {
            update: {
              Status: {
                connect: {
                  id: statuses.find((status: any) => status.key === 'LICENSE_UPLOADED').id
                }
              },
            }
          },
        }

        updatePharmacy({ variables: { data: deaLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
          updatePharmacy({ variables: { data: ncpdpLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
            window.location.reload();
          }).catch((error: any) => {
            addToast({ type: NotificationType.Error, title: error.message  });
          });
        }).catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message  });
        });
      } else {
        setUploadLoading(false);
      }
    } catch (error: any) {
      setUploadLoading(false);
      addToast({ type: NotificationType.Error, title: error.message  });
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography component="h1" variant="h5" sx={{mb: 3}}>Pharmacy Licenses</Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12} md={6} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <Button size='large' variant="contained" component="label" sx={{fontSize: 14}}>
            <FileUploadOutlinedIcon sx={{fontSize: '18px', mr: 1}} />
            Upload DEA License
            <input hidden accept="image/jpeg,image/jpg,image/png,.pdf" multiple type="file" onChange={handleDeaLicenseFileChange} />
          </Button>
          {deaLicenseFile && (
            <Grid sx={{width: '280px', fontSize: 14, textAlign: 'center', fontWeight: '600', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', mt: 2}}>
              <Link sx={{}} href={`${SERVER_URL}/file/${deaLicenseDefaultFile.File.key}`} color="inherit" target='_blank'>
                {deaLicenseFile.name}
              </Link>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={6} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <Button size='large' variant="contained" component="label" sx={{fontSize: 14}}>
            <FileUploadOutlinedIcon sx={{fontSize: '18px', mr: 1}} />
            Upload Pharmacy License
            <input hidden accept="image/jpeg,image/jpg,image/png,.pdf" multiple type="file" onChange={handleNcpdpLicenseFileChange} />
          </Button>
          {ncpdpLicenseFile && (
            <Grid sx={{width: '280px', fontSize: 14, textAlign: 'center', fontWeight: '600', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', mt: 2}}>
              <Link sx={{}} href={`${SERVER_URL}/file/${ncpdpLicenseDefaultFile.File.key}`} color="inherit" target='_blank'>
                {ncpdpLicenseFile.name}
              </Link>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} sx={{width: '100%',display: 'flex', justifyContent: 'center', mt: 4}}>
          <Button size='large' variant="contained" sx={{width: '200px'}} onClick={handleSave}>
            Save
            {(loading || uploadLoading) && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PharmacyFiles;